import React from 'react';
import { Plus, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { useProgramForm } from '../../../context/ProgramFormContext';
import Field from './ui/Field';
import Toggle from './ui/Toggle';
import { TextInput, TextareaInput, NumberInput, SelectInput } from './ui/Inputs';
import { s, brand, gray, ink, white, radius } from './theme';

/**
 * Custom question editor. Ports `AddProgramModal.js:2549-2715` and its handlers at
 * `:1345-1520`.
 *
 * Question types drive what else appears:
 *   1 Short Answer                    — no options
 *   2 Drop-down, 3 Checkboxes, 4 Multiple Choice — a plain option list
 *   5 Multiple Choice with Discounts, 6 Drop-down with Discounts — options carry a
 *     discount type and amount
 *
 * "Is Other Option" only applies to types 3 and 4. "Is Conditional" only appears from the
 * second question on, since a question can only depend on one before it.
 */

export const questionTypes = [
    { label: 'Short Answer', value: 1 },
    { label: 'Drop-down', value: 2 },
    { label: 'Checkboxes', value: 3 },
    { label: 'Multiple Choice', value: 4 },
    { label: 'Multiple Choice with Discounts', value: 5 },
    { label: 'Drop-down with Discounts', value: 6 },
];

export const operationType = [
    { label: 'All answers Required', value: 2 },
    { label: 'Atleast one answer required', value: 1 },
];

const amountOpt = [
    { value: 1, label: 'Amount' },
    { value: 2, label: 'Percentage' },
];

const money = (value, type) =>
    type === 1 ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : `${value}%`;

const QuestionCard = ({ item, index }) => {
    const { cmQuestions, setCmQuestions, setCmQuestionsErr, dependsOnOptions, setDependsOnOptions } =
        useProgramForm();

    const i = index;
    const hasOptions = [2, 3, 4, 5, 6].includes(item.questionType);
    const hasDiscounts = item.questionType === 5 || item.questionType === 6;
    const canHaveOther = item.questionType === 3 || item.questionType === 4;

    const patch = mutate => {
        const temp = [...cmQuestions];
        mutate(temp);
        setCmQuestions(temp);
    };

    // Changing the type resets the question — new id, no options, no title. Regenerating
    // the id is what the old form does (`AddProgramModal.js:1372-1384`); it is preserved
    // deliberately even though it orphans any conditional pointing at this question.
    const handleQuestionTypeChange = e =>
        patch(temp => {
            temp[i] = {
                questionId: uuidv4(),
                questionType: e.value,
                title: '',
                subTitle: '',
                optionVal: '',
                options: [],
                isRequred: 2,
                isOtherOption: 2,
            };
        });

    const handleQuestionChange = e => {
        patch(temp => {
            temp[i].title = e.target.value;
        });
        setCmQuestionsErr('');
    };

    const handleAddOption = () => {
        const temp = [...cmQuestions];
        if (!temp[i].optionVal || !temp[i].optionVal.trim()) return;

        if (temp[i].options.filter(o => o.opt === temp[i].optionVal).length) {
            temp[i].optionValErr = 'duplicate value not allowed';
            setCmQuestions(temp);
            return;
        }

        temp[i].options.push({ opt: temp[i].optionVal });
        temp[i].optionVal = '';
        setCmQuestions(temp);
    };

    const handleDependsOnChange = e => {
        patch(temp => {
            temp[i] = { ...temp[i], conditionalOn: [{ questionId: e.value }] };
        });
        setDependsOnOptions(prev => prev.filter(option => option.value !== e.value));
    };

    const handleDependsOnChange2 = e =>
        patch(temp => {
            temp[i] = {
                ...temp[i],
                conditionalOn: [
                    { ...temp[i]?.conditionalOn[0], optionVals: e ? e.map(o => o.value) : '' },
                ],
            };
        });

    const handleDependsOnChange3 = e =>
        patch(temp => {
            temp[i] = {
                ...temp[i],
                conditionalOn: [{ ...temp[i]?.conditionalOn[0], optionValOperator: e.value }],
            };
        });

    // The answers a conditional question can match against — only list-type questions
    // have any.
    const getOptionsValues = qId => {
        if (!qId) return [];
        const source = cmQuestions.filter(ques => ques.questionId === qId)[0];
        if (source?.questionType === 1) return [];
        return source?.options.map(opts => ({ label: opts.opt, value: opts.opt })) || [];
    };

    const conditionalOn = item.conditionalOn && item.conditionalOn[0];

    return (
        <div
            style={{
                position: 'relative',
                borderRadius: radius.lg,
                border: `1px solid ${gray[200]}`,
                backgroundColor: white,
                padding: '16px',
                marginBottom: '12px',
            }}
        >
            <div
                className="d-flex flex-wrap justify-content-between align-items-center mb-3"
                style={{ gap: '12px' }}
            >
                <label style={{ ...s.label, marginBottom: 0 }}>Question {i + 1}</label>

                <div className="d-flex flex-wrap align-items-center" style={{ gap: '16px' }}>
                    {canHaveOther ? (
                        <Toggle
                            id={`isOtherOption-${i}`}
                            label="Is Other Option?"
                            checked={item.isOtherOption === 1}
                            onChange={() =>
                                patch(temp => {
                                    temp[i].isOtherOption = temp[i].isOtherOption === 1 ? 2 : 1;
                                })
                            }
                            labelStyle={{ color: ink[600] }}
                        />
                    ) : ''}

                    {i > 0 ? (
                        <Toggle
                            id={`conditional-${i}`}
                            label="Is Conditional?"
                            checked={item.conditional === 1}
                            onChange={() =>
                                patch(temp => {
                                    temp[i].conditional = temp[i].conditional === 1 ? 2 : 1;
                                    temp[i].conditionalOn = [];
                                })
                            }
                            labelStyle={{ color: ink[600] }}
                        />
                    ) : ''}

                    <Toggle
                        id={`isRequred-${i}`}
                        label="Is Mandatory?"
                        checked={item.isRequred === 1}
                        onChange={() =>
                            patch(temp => {
                                temp[i].isRequred = temp[i].isRequred === 1 ? 2 : 1;
                            })
                        }
                        labelStyle={{ color: ink[600] }}
                    />

                    <div style={{ minWidth: '220px' }}>
                        <SelectInput
                            id={`questionType-${i}`}
                            pHolder="Select Question Type"
                            value={questionTypes.filter(t => t.value === item.questionType)}
                            change={handleQuestionTypeChange}
                            options={questionTypes}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={() =>
                            setCmQuestions(cmQuestions.filter((q, idx) => idx !== i))
                        }
                        aria-label={`Remove question ${i + 1}`}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            color: ink[400],
                            cursor: 'pointer',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = brand[600]; }}
                        onMouseLeave={e => { e.currentTarget.style.color = ink[400]; }}
                    >
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>
            </div>

            {/* Conditional display rules */}
            {item.conditional === 1 ? (
                <div className="row mb-3">
                    <Field
                        className="col-12 col-lg-4 mb-3"
                        label="Conditional Question"
                        htmlFor={`dependsOn-${i}`}
                    >
                        <SelectInput
                            id={`dependsOn-${i}`}
                            pHolder="Select conditional question"
                            value={
                                conditionalOn?.questionId
                                    ? dependsOnOptions.filter(o => o.value === conditionalOn.questionId)
                                    : ''
                            }
                            change={handleDependsOnChange}
                            options={dependsOnOptions.filter(o => o.value !== item.questionId)}
                        />
                    </Field>

                    {conditionalOn?.questionId ? (
                        <>
                            <Field
                                className="col-12 col-sm-6 col-lg-4 mb-3"
                                label="Match Answers"
                                htmlFor={`matchAnswers-${i}`}
                            >
                                <SelectInput
                                    id={`matchAnswers-${i}`}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    pHolder="Select all required answers"
                                    change={handleDependsOnChange2}
                                    value={
                                        conditionalOn?.optionVals?.length > 0
                                            ? conditionalOn.optionVals.map(opt => ({ label: opt, value: opt }))
                                            : ''
                                    }
                                    options={getOptionsValues(conditionalOn?.questionId)}
                                />
                            </Field>

                            <Field
                                className="col-12 col-sm-6 col-lg-4 mb-3"
                                label="Match Type"
                                htmlFor={`matchType-${i}`}
                            >
                                <SelectInput
                                    id={`matchType-${i}`}
                                    pHolder="Match type"
                                    value={
                                        operationType.filter(
                                            o => o.value === conditionalOn?.optionValOperator
                                        )[0] || ''
                                    }
                                    change={handleDependsOnChange3}
                                    options={operationType}
                                />
                            </Field>
                        </>
                    ) : ''}
                </div>
            ) : ''}

            {/* Question text — only once a type is chosen, as in the old form */}
            {item.questionType ? (
                <div className="row">
                    <Field className="col-md-6 mb-3" label="Question" htmlFor={`qTitle-${i}`} required>
                        <TextareaInput
                            id={`qTitle-${i}`}
                            value={item.title}
                            change={handleQuestionChange}
                            pHolder="Question"
                            rows={2}
                        />
                    </Field>

                    <Field className="col-md-6 mb-3" label="Sub Title" htmlFor={`qSub-${i}`}>
                        <TextareaInput
                            id={`qSub-${i}`}
                            value={item.subTitle}
                            change={e =>
                                patch(temp => {
                                    temp[i].subTitle = e.target.value;
                                })
                            }
                            pHolder="Sub Title"
                            rows={2}
                        />
                    </Field>
                </div>
            ) : ''}

            {/* Options */}
            {hasOptions ? (
                <div>
                    {item.options.length ? (
                        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                            {item.options.map((itm, j) => (
                                <li
                                    key={`${itm.opt}-${j}`}
                                    className="d-flex flex-wrap align-items-center"
                                    style={{
                                        gap: '12px',
                                        padding: '8px 12px',
                                        marginBottom: '6px',
                                        borderRadius: radius.md,
                                        backgroundColor: gray[50],
                                        border: `1px solid ${gray[200]}`,
                                    }}
                                >
                                    <span style={{ flex: 1, minWidth: '120px', fontSize: '13px', color: ink[800] }}>
                                        {itm.opt}
                                    </span>

                                    {hasDiscounts ? (
                                        <>
                                            <div style={{ minWidth: '150px' }}>
                                                <SelectInput
                                                    pHolder="Select a type"
                                                    change={e =>
                                                        patch(temp => {
                                                            temp[i].options[j].discountType = e.value;
                                                        })
                                                    }
                                                    value={amountOpt.filter(a => a.value === itm.discountType)}
                                                    options={amountOpt}
                                                />
                                            </div>
                                            <div style={{ minWidth: '130px' }}>
                                                <NumberInput
                                                    change={e =>
                                                        patch(temp => {
                                                            temp[i].options[j].discount = e;
                                                        })
                                                    }
                                                    formatter={value => money(value, itm.discountType)}
                                                    value={itm.discount ? itm.discount : ''}
                                                    pHolder="Discount"
                                                    min={0}
                                                    max={itm.discountType === 1 ? Number.MAX_VALUE : 100}
                                                />
                                            </div>
                                        </>
                                    ) : ''}

                                    <button
                                        type="button"
                                        onClick={() =>
                                            patch(temp => {
                                                temp[i].options.splice(j, 1);
                                            })
                                        }
                                        aria-label={`Remove option ${itm.opt}`}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '4px',
                                            color: ink[400],
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <X size={14} aria-hidden="true" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : ''}

                    <div className="d-flex align-items-start" style={{ gap: '12px' }}>
                        <div style={{ flex: 1, maxWidth: '360px' }}>
                            <TextInput
                                value={item.optionVal || ''}
                                change={e =>
                                    patch(temp => {
                                        temp[i].optionVal = e.target.value;
                                        temp[i].optionValErr = '';
                                    })
                                }
                                pHolder="Add Option"
                                error={item.optionValErr}
                            />
                            {item.optionValErr ? (
                                <p style={s.errorText}>{item.optionValErr}</p>
                            ) : ''}
                        </div>
                        <button type="button" style={s.buttonSecondary} onClick={handleAddOption}>
                            <Plus size={14} aria-hidden="true" />
                            Add
                        </button>
                    </div>
                </div>
            ) : ''}
        </div>
    );
};

export default QuestionCard;
